import React from "react";
import { Box, Typography, Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Chip } from "@mui/material";
import { Delete } from "@mui/icons-material";
import AxiosInstance from "../../services/networkServices";
import CustomMultiSelect from "../../components/FormElements/MultiSelect";
import { showToast } from "../../store/slices/common";

const EditEventDialog = ({ editModalOpen, handleCloseModal, selectedEvent, editFormData, handleFormChange, allAudios, selectedAudioIds, setSelectedAudioIds, saving, setSaving, dispatch, fetchChallenges, fetchContests, handleSave }) => (
<>
      {/* Edit Modal */}
      <Dialog 
        open={editModalOpen} 
        onClose={handleCloseModal}
        maxWidth="sm"
        fullWidth
        sx={{
          "& .MuiDialog-paper": {
            borderRadius: "12px",
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 600, borderBottom: "1px solid #eee" }}>
          Edit {selectedEvent?.type === "challenge" ? "Challenge" : "Contest"}
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {selectedEvent?.type === "challenge" ? (
            <>
              <TextField
                label="Title"
                value={editFormData.title || ""}
                onChange={(e) => handleFormChange("title", e.target.value)}
                fullWidth
                margin="normal"
                size="small"
              />
              <TextField
                label="Start Date & Time"
                type="datetime-local"
                value={editFormData.startDate || ""}
                onChange={(e) => handleFormChange("startDate", e.target.value)}
                fullWidth
                margin="normal"
                size="small"
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="End Date & Time"
                type="datetime-local"
                value={editFormData.endDate || ""}
                onChange={(e) => handleFormChange("endDate", e.target.value)}
                fullWidth
                margin="normal"
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </>
          ) : (
            <>
              <TextField
                label="Title"
                value={editFormData.title || ""}
                onChange={(e) => handleFormChange("title", e.target.value)}
                fullWidth
                margin="normal"
                size="small"
              />
              <TextField
                label="Prize"
                value={editFormData.prize || ""}
                onChange={(e) => handleFormChange("prize", e.target.value)}
                fullWidth
                margin="normal"
                size="small"
              />
              <TextField
                label="Details"
                value={editFormData.details || ""}
                onChange={(e) => handleFormChange("details", e.target.value)}
                fullWidth
                margin="normal"
                size="small"
                multiline
                rows={3}
              />
              <TextField
                label="Voting Start Date & Time"
                type="datetime-local"
                value={editFormData.votingStartDate || ""}
                onChange={(e) => handleFormChange("votingStartDate", e.target.value)}
                fullWidth
                margin="normal"
                size="small"
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="Voting End Date & Time"
                type="datetime-local"
                value={editFormData.votingEndDate || ""}
                onChange={(e) => handleFormChange("votingEndDate", e.target.value)}
                fullWidth
                margin="normal"
                size="small"
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="Show in Category List Date & Time"
                type="datetime-local"
                value={editFormData.showInCategoriesListFromDate || ""}
                onChange={(e) => handleFormChange("showInCategoriesListFromDate", e.target.value)}
                fullWidth
                margin="normal"
                size="small"
                InputLabelProps={{ shrink: true }}
                helperText="When this contest becomes visible in the app"
              />
              
              {/* Audio Assignment */}
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" sx={{ color: "#666", fontSize: "14px", mb: 1 }}>
                  Assigned Audios
                </Typography>
                {allAudios.length > 0 ? (
                  <CustomMultiSelect
                    selectOptions={allAudios.map(audio => ({
                      _id: audio._id,
                      name: audio.name,
                      image: audio.image
                    }))}
                    onChange={(newSelected) => setSelectedAudioIds(newSelected)}
                    placeholder="Select Audios"
                    value={selectedAudioIds}
                  />
                ) : (
                  <Typography variant="body2" sx={{ color: "#999", fontSize: "13px", fontStyle: "italic" }}>
                    No audios available
                  </Typography>
                )}
                {selectedAudioIds.length > 0 && (
                  <Box sx={{ mt: 1, display: "flex", flexWrap: "wrap", gap: 1 }}>
                    {selectedAudioIds.map(audioId => {
                      const audio = allAudios.find(a => a._id === audioId);
                      if (!audio) return null;
                      return (
                        <Chip
                          key={audioId}
                          avatar={<img src={audio.image} alt="" style={{ width: 24, height: 24, borderRadius: "50%" }} />}
                          label={audio.name?.length > 20 ? `${audio.name.slice(0, 20)}...` : audio.name}
                          size="small"
                          onDelete={() => setSelectedAudioIds(prev => prev.filter(id => id !== audioId))}
                          sx={{
                            backgroundColor: "#e3f2fd",
                            "& .MuiChip-label": { fontSize: "12px" }
                          }}
                        />
                      );
                    })}
                  </Box>
                )}
              </Box>
            </>
          )}
          
          {/* Show current info */}
          <Box sx={{ mt: 2, p: 2, backgroundColor: "#f5f5f5", borderRadius: "8px" }}>
            <Typography variant="caption" color="#666" display="block">
              Category: {selectedEvent?.category || "N/A"}
            </Typography>
            {selectedEvent?.submissionCount != null && (
              <Typography variant="caption" color="#666" display="block">
                {selectedEvent?.type === "challenge" ? "Posts" : "Entries"}: {selectedEvent?.submissionCount}
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: "1px solid #eee", justifyContent: "space-between" }}>
          {/* Delete button - show for both challenges and contests */}
          <Button 
            startIcon={<Delete />}
            onClick={async () => {
              if (!selectedEvent?.rawId) return;
              try {
                setSaving(true);
                if (selectedEvent.type === "challenge") {
                  await AxiosInstance.delete(`/category/challenge/${selectedEvent.rawId}`);
                  dispatch(showToast({ color: "success", msg: "Challenge deleted successfully" }));
                } else {
                  await AxiosInstance.delete(`/contest/${selectedEvent.rawId}`);
                  dispatch(showToast({ color: "success", msg: "Contest deleted successfully" }));
                }
                await Promise.all([fetchChallenges(), fetchContests()]);
                handleCloseModal();
              } catch (error) {
                console.error("Error deleting:", error);
                dispatch(showToast({ 
                  color: "error", 
                  msg: error.response?.data?.message || `Failed to delete ${selectedEvent?.type}` 
                }));
              } finally {
                setSaving(false);
              }
            }}
            disabled={saving}
            sx={{ 
              color: "#d32f2f",
              borderColor: "#d32f2f",
              border: "1px solid #d32f2f",
              "&:hover": { 
                backgroundColor: "#ffebee",
                borderColor: "#b71c1c",
                color: "#b71c1c",
              },
            }}
          >
            Delete
          </Button>
          <Box>
            <Button 
              onClick={handleCloseModal}
              sx={{ color: "#666", mr: 1 }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSave}
              variant="contained"
              disabled={saving}
              sx={{ 
                backgroundColor: "#142C5C",
                "&:hover": { backgroundColor: "#0f2247" },
              }}
            >
              {saving ? "Saving..." : "Save Changes"}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
</>
);

export default EditEventDialog;
